import { useEffect, useState } from 'react';
import { Play, Image as ImageIcon, User, Mail, X, Download, Check } from 'lucide-react';
import { formatDateTime } from '../../utils/formatters';

function GalleryVideoCard({
  video,
  isClientMode = false,
  isSelectionMode = false,
  isSelected = false,
  onToggleSelect,
  onDownload
}) {
  const [showPlayer, setShowPlayer] = useState(false);
  const isImage = video?.mimeType?.startsWith('image/');
  const downloadLabel = isImage ? 'Download Photo' : 'Download Video';

  useEffect(() => {
    if (!showPlayer) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowPlayer(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showPlayer]);

  const handleCardClick = () => {
    if (isSelectionMode) {
      onToggleSelect?.(video.id);
    } else {
      setShowPlayer(true);
    }
  };

  const handleClosePlayer = () => {
    setShowPlayer(false);
  };

  const handleDownloadClick = (e) => {
    e.stopPropagation();
    onDownload?.(video);
  };

  const uploadedAtFormatted = video.uploadedAt?.toDate
    ? formatDateTime(video.uploadedAt.toDate())
    : 'Just now';

  return (
    <>
      <div
        className={`group cursor-pointer bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden border transition-all hover:shadow-xl ${
          isSelected
            ? 'border-gold ring-2 ring-gold/50 shadow-gold/20'
            : 'border-white/10 hover:border-gold/50 hover:shadow-gold/20'
        }`}
        onClick={handleCardClick}
      >
        {/* Media Thumbnail */}
        <div className="relative aspect-video bg-black">
          {isImage ? (
            <img
              src={video.videoUrl}
              alt={`${video.guestName}'s upload`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <video
              src={`${video.videoUrl}#t=0.1`}
              className="w-full h-full object-cover"
              preload="metadata"
              muted
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:from-black/90 transition-all" />

          {/* Selection Checkbox - top left */}
          {isSelectionMode && (
            <div className="absolute top-3 left-3 z-10">
              <div
                className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all ${
                  isSelected
                    ? 'bg-gold border-gold'
                    : 'bg-black/50 border-white/50 group-hover:border-white'
                }`}
              >
                {isSelected && <Check className="w-4 h-4 text-dark-slate" />}
              </div>
            </div>
          )}

          {/* Download Button - top right (client mode, not selection mode) */}
          {isClientMode && !isSelectionMode && (
            <button
              onClick={handleDownloadClick}
              className="absolute top-3 right-3 z-10 p-2 bg-black/60 hover:bg-gold text-white hover:text-dark-slate rounded-full opacity-0 group-hover:opacity-100 transition-all"
              title={downloadLabel}
            >
              <Download className="w-4 h-4" />
            </button>
          )}

          {/* Preview Button */}
          {!isSelectionMode && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-2xl shadow-gold/50">
                {isImage ? (
                  <ImageIcon className="w-8 h-8 text-dark-slate" />
                ) : (
                  <Play className="w-8 h-8 text-dark-slate ml-1" fill="currentColor" />
                )}
              </div>
            </div>
          )}

          {/* Guest Name Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div className="flex items-center gap-2 text-white">
              <User className="w-4 h-4 text-gold" />
              <span className="font-semibold">{video.guestName}</span>
            </div>
          </div>
        </div>

        {/* Video Info */}
        <div className="p-4 space-y-2">
          {video.guestEmail && (
            <div className="flex items-center gap-2 text-sm text-neutral-grey-300">
              <Mail className="w-4 h-4 text-gold" />
              <span className="truncate">{video.guestEmail}</span>
            </div>
          )}
          <p className="text-xs text-neutral-grey-400">{uploadedAtFormatted}</p>
        </div>
      </div>

      {/* Media Viewer Modal */}
      {showPlayer && (
        <div
          className="fixed inset-0 bg-black/95 flex items-center justify-center z-50 p-3 sm:p-4 animate-fadeIn overflow-y-auto"
          onClick={handleClosePlayer}
        >
          <div
            className="relative w-full max-w-4xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Media Viewer */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black min-h-[220px] flex items-center justify-center">
              {/* Always-visible close button */}
              <button
                onClick={handleClosePlayer}
                className="absolute top-3 right-3 z-20 p-2 rounded-full bg-black/65 text-white hover:text-gold hover:bg-black/80 transition-colors"
                title="Close viewer"
                aria-label="Close viewer"
              >
                <X className="w-5 h-5" />
              </button>

              {isImage ? (
                <img
                  src={video.videoUrl}
                  alt={`${video.guestName}'s upload`}
                  className="max-h-[72vh] w-auto max-w-full object-contain"
                />
              ) : (
                <video
                  src={video.videoUrl}
                  controls
                  autoPlay
                  muted
                  className="w-full max-h-[72vh] object-contain bg-black"
                />
              )}
            </div>

            {/* Video Details */}
            <div className="mt-4 bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 max-w-2xl mx-auto w-full">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-white">{video.guestName}</p>
                    {video.guestEmail && (
                      <p className="text-sm text-neutral-grey-300">{video.guestEmail}</p>
                    )}
                  </div>
                </div>
                {isClientMode && (
                  <button
                    onClick={() => onDownload?.(video)}
                    className="px-4 py-2 bg-gold hover:bg-gold-light text-dark-slate font-semibold rounded-lg transition-all flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    {downloadLabel}
                  </button>
                )}
              </div>
              <div className="mt-3 flex items-center justify-between gap-3">
                <p className="text-sm text-neutral-grey-400">{uploadedAtFormatted}</p>
                <button
                  onClick={handleClosePlayer}
                  className="text-sm text-white/80 hover:text-gold transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </>
  );
}

export default GalleryVideoCard;
